import React from "react";
import { observer } from "mobx-react-lite";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import {
  FooterContainer,
  FooterSkipButton,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { navigateToExternalWindowUrl } from "@/modules/window/navigation";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSIcon, MDSIconKind, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";

import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { logger } from "@/modules/logger";
import {
  AppDownloadButton,
  AppIconWrapper,
} from "@/components/onboarding/components/app-download-button";

const getOsConfig = ({
  isMacBrowser,
  isWindowsBrowser,
  isIosBrowser,
}: {
  isMacBrowser: boolean;
  isWindowsBrowser: boolean;
  isIosBrowser: boolean;
}): { appName: string | null; downloadUrl: string | null } => {
  const appName = isMacBrowser
    ? "Mac app"
    : isWindowsBrowser
      ? "Windows app"
      : isIosBrowser
        ? "iOS app"
        : null;

  const downloadUrl = isMacBrowser
    ? APP_CONFIG.URLS.MAC_APP_ONBOARDING
    : isWindowsBrowser
      ? APP_CONFIG.URLS.WINDOWS_APP_ONBOARDING
      : isIosBrowser
        ? APP_CONFIG.URLS.IOS_APP_ONBOARDING
        : null;

  return {
    appName: appName,
    downloadUrl,
  };
};

export const NativeAppDownloadStep: React.FC<StepRenderProps> = observer(({ onComplete }) => {
  const isMacBrowser = clientEnvModule.isMacBrowser();
  const isWindowsBrowser = clientEnvModule.isWindowsBrowser();
  const isIosBrowser = clientEnvModule.isIosBrowser();

  const { appName, downloadUrl } = getOsConfig({
    isMacBrowser,
    isWindowsBrowser,
    isIosBrowser,
  });

  const handleDownload = async () => {
    if (!downloadUrl) {
      logger.error({
        message: "Download URL is null in NativeAppDownloadStep",
        info: {
          appName,
          downloadUrl,
        },
      });

      return;
    }

    trackEvent(TrackedEvent.NativeUpsellDownload, {
      platform: appName,
    });

    onComplete();
    await navigateToExternalWindowUrl({ url: new URL(downloadUrl) });
  };

  const handleSkip = (untracked: boolean = false) => {
    if (!untracked) {
      trackEvent(TrackedEvent.NativeUpsellSkip, {
        platform: appName,
      });
    }

    onComplete();
  };

  useEffectOnMount(() => {
    trackEvent(TrackedEvent.NativeUpsellView, {
      platform: appName,
    });
  });

  useEffectOnMount(() => {
    if (!downloadUrl || !appName) {
      // this would be highly exceptional
      logger.error({
        message: "Download URL or app name is null in NativeAppDownloadStep",
        info: {
          downloadUrl,
          appName,
        },
      });

      handleSkip(true);
    }
  });

  if (!downloadUrl || !appName) {
    // we call handleSkip in this case in the effect above
    return null;
  }

  return (
    <WizardContentWrapper>
      <StepTitle>Mem is better on the {appName}</StepTitle>

      <StepContent>
        <div>
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
            Take notes offline, record better meeting notes, and more
          </MDSText>
        </div>

        <AppDownloadButton onClick={handleDownload}>
          <AppIconWrapper data-icon-wrapper isApple={isMacBrowser || isIosBrowser}>
            <MDSIcon
              kind={
                isMacBrowser
                  ? MDSIconKind.BrandApple
                  : isWindowsBrowser
                    ? MDSIconKind.BrandWindows
                    : MDSIconKind.BrandApple
              }
              size={16}
            />
          </AppIconWrapper>
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
            Download {appName}
          </MDSText>
        </AppDownloadButton>
      </StepContent>

      <FooterContainer align="flex-end">
        <FooterSkipButton onClick={() => handleSkip()}>Skip for now</FooterSkipButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});
